import React, { useState, useEffect } from 'react';
import { Phone, PhoneIncoming, PhoneOff, Zap, TrendingUp, BarChart3, Users } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface AIAgentsContentProps {
  animationDelay?: number;
}

const AIAgentsContent: React.FC<AIAgentsContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [totalCalls, setTotalCalls] = useState(0);
  const [actionsTriggered, setActionsTriggered] = useState(0);
  const [sentiment, setSentiment] = useState(0);
  const [callState, setCallState] = useState<'idle' | 'incoming' | 'active' | 'completed'>('idle');
  const [callDuration, setCallDuration] = useState(0);

  useEffect(() => {
    // Animate counters
    const callsInterval = setInterval(() => {
      setTotalCalls(prev => {
        if (prev >= 847) return 847;
        return prev + Math.ceil((847 - prev) / 8);
      });
    }, 50);

    const actionsInterval = setInterval(() => {
      setActionsTriggered(prev => {
        if (prev >= 1234) return 1234;
        return prev + Math.ceil((1234 - prev) / 8);
      });
    }, 50);

    const sentimentInterval = setInterval(() => {
      setSentiment(prev => {
        if (prev >= 92) return 92;
        return prev + 2;
      });
    }, 40);

    // Incoming call animation
    const callTimer = setTimeout(() => {
      setCallState('incoming');
      setTimeout(() => setCallState('active'), 1500);
      setTimeout(() => {
        const durationInterval = setInterval(() => {
          setCallDuration(prev => {
            if (prev >= 45) {
              clearInterval(durationInterval);
              return 45;
            }
            return prev + 1;
          });
        }, 100);
      }, 1500);
      setTimeout(() => setCallState('completed'), 4000);
    }, animationDelay + 1200);

    return () => {
      clearInterval(callsInterval);
      clearInterval(actionsInterval);
      clearInterval(sentimentInterval);
      clearTimeout(callTimer);
    };
  }, [animationDelay]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className="flex-1 flex flex-col overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            AI Agents
          </span>
          <span 
            className="text-[7px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5"
            style={{ 
              backgroundColor: '#10B98120',
              color: '#10B981',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          >
            <span className="w-1 h-1 rounded-full bg-[#10B981]" style={{ animation: 'pulse 1s ease-in-out infinite' }} />
            Live
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span 
            className="text-[7px] px-1.5 py-0.5 rounded"
            style={{ 
              backgroundColor: isDark ? '#8B5CF620' : '#8B5CF610',
              color: '#8B5CF6'
            }}
          >
            + Create Agent
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div 
        className="grid grid-cols-2 lg:grid-cols-3 gap-1 lg:gap-1.5 p-1.5 lg:p-2"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both` }}
      >
        {/* Total Calls */}
        <div 
          className="rounded-lg p-1.5 lg:p-2"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `scaleIn 0.3s ease-out ${animationDelay + 300}ms both`
          }}
        >
          <div className="flex items-center gap-1 mb-0.5 lg:mb-1">
            <Phone className="w-2 h-2 lg:w-2.5 lg:h-2.5" style={{ color: '#3B82F6' }} />
            <span className="text-[6px] lg:text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>Calls</span>
          </div>
          <div className="flex items-baseline gap-0.5 lg:gap-1">
            <span className="text-sm lg:text-lg font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              {totalCalls.toLocaleString()}
            </span>
            <span className="text-[5px] lg:text-[6px] flex items-center" style={{ color: '#10B981' }}>
              <TrendingUp className="w-1.5 h-1.5 lg:w-2 lg:h-2" />+12%
            </span>
          </div>
        </div>

        {/* Actions Triggered */}
        <div 
          className="rounded-lg p-1.5 lg:p-2"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `scaleIn 0.3s ease-out ${animationDelay + 400}ms both`
          }}
        >
          <div className="flex items-center gap-1 mb-0.5 lg:mb-1">
            <Zap className="w-2 h-2 lg:w-2.5 lg:h-2.5" style={{ color: '#F59E0B' }} />
            <span className="text-[6px] lg:text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>Actions</span>
          </div>
          <div className="flex items-baseline gap-0.5 lg:gap-1">
            <span className="text-sm lg:text-lg font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              {actionsTriggered.toLocaleString()}
            </span>
            <span className="text-[5px] lg:text-[6px] flex items-center" style={{ color: '#10B981' }}>
              <TrendingUp className="w-1.5 h-1.5 lg:w-2 lg:h-2" />+8%
            </span>
          </div>
        </div>

        {/* Sentiment - hidden on mobile */}
        <div 
          className="hidden lg:block rounded-lg p-1.5 lg:p-2"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `scaleIn 0.3s ease-out ${animationDelay + 500}ms both`
          }}
        >
          <div className="flex items-center gap-1 mb-0.5 lg:mb-1">
            <BarChart3 className="w-2 h-2 lg:w-2.5 lg:h-2.5" style={{ color: '#10B981' }} />
            <span className="text-[6px] lg:text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>Sentiment</span>
          </div>
          <div className="flex items-baseline gap-0.5 lg:gap-1">
            <span className="text-sm lg:text-lg font-bold" style={{ color: '#10B981' }}>
              {sentiment}%
            </span>
            <span className="text-[5px] lg:text-[6px]" style={{ color: isDark ? '#666666' : '#999999' }}>Positive</span>
          </div>
        </div>
      </div>

      {/* Active Calls Section */}
      <div 
        className="flex-1 mx-2 mb-2 rounded-lg overflow-hidden"
        style={{ 
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
          animation: `fadeIn 0.4s ease-out ${animationDelay + 600}ms both`
        }}
      >
        <div 
          className="px-2 py-1.5 border-b flex items-center justify-between"
          style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
        >
          <span className="text-[8px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            Live Activity
          </span>
          <span 
            className="text-[6px] px-1 py-0.5 rounded"
            style={{ 
              backgroundColor: isDark ? '#2A2A2A' : '#F0F0F0',
              color: isDark ? '#888888' : '#666666'
            }}
          >
            3 active
          </span>
        </div>

        <div className="p-2 space-y-1.5">
          {/* Incoming/Active Call Card */}
          <div 
            className="rounded-lg p-2 relative overflow-hidden"
            style={{ 
              backgroundColor: callState === 'incoming' 
                ? (isDark ? '#3B82F620' : '#3B82F610')
                : callState === 'active'
                  ? (isDark ? '#10B98120' : '#10B98110')
                  : (isDark ? '#1A1A1A' : '#F9F9F9'),
              border: `1px solid ${
                callState === 'incoming' ? '#3B82F6' 
                : callState === 'active' ? '#10B981' 
                : (isDark ? '#333333' : '#E5E5E5')
              }`,
              transition: 'all 0.3s ease'
            }}
          >
            {/* Ring animation for incoming */}
            {callState === 'incoming' && (
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at center, #3B82F630 0%, transparent 70%)`,
                  animation: 'pulse 0.8s ease-in-out infinite'
                }}
              />
            )}

            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-1.5">
                <div 
                  className="w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ 
                    backgroundColor: callState === 'incoming' ? '#3B82F620' 
                      : callState === 'active' ? '#10B98120' 
                      : '#6B728020'
                  }}
                >
                  {callState === 'incoming' ? (
                    <PhoneIncoming className="w-2.5 h-2.5" style={{ color: '#3B82F6', animation: 'shake 0.5s ease-in-out infinite' }} />
                  ) : callState === 'active' ? (
                    <Phone className="w-2.5 h-2.5" style={{ color: '#10B981' }} />
                  ) : callState === 'completed' ? (
                    <PhoneOff className="w-2.5 h-2.5" style={{ color: '#6B7280' }} />
                  ) : (
                    <Phone className="w-2.5 h-2.5" style={{ color: '#6B7280' }} />
                  )}
                </div>
                <div>
                  <div className="text-[7px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                    {callState === 'completed' ? 'Call Completed' : 'Sarah Johnson'}
                  </div>
                  <div className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                    {callState === 'incoming' ? 'Incoming call...' 
                      : callState === 'active' ? `Duration: ${formatDuration(callDuration)}`
                      : callState === 'completed' ? 'Successfully handled'
                      : 'Waiting...'}
                  </div>
                </div>
              </div>
              <div 
                className="text-[6px] px-1.5 py-0.5 rounded"
                style={{ 
                  backgroundColor: callState === 'incoming' ? '#3B82F620'
                    : callState === 'active' ? '#10B98120'
                    : callState === 'completed' ? '#10B98120'
                    : (isDark ? '#2A2A2A' : '#F0F0F0'),
                  color: callState === 'incoming' ? '#3B82F6'
                    : callState === 'active' ? '#10B981'
                    : callState === 'completed' ? '#10B981'
                    : (isDark ? '#888888' : '#666666')
                }}
              >
                {callState === 'incoming' ? 'Ringing' 
                  : callState === 'active' ? 'Live'
                  : callState === 'completed' ? '✓ Done'
                  : 'Idle'}
              </div>
            </div>
          </div>

          {/* Other agent cards */}
          {[
            { name: 'Support Agent', status: 'Handling inquiry', type: 'Active' },
            { name: 'Sales Agent', status: 'Qualifying lead', type: 'Active' },
          ].map((agent, i) => (
            <div 
              key={i}
              className="rounded-lg p-2"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F9F9F9',
                border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                animation: `fadeIn 0.3s ease-out ${animationDelay + 800 + (i * 100)}ms both`
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full bg-[#8B5CF620] flex items-center justify-center">
                    <Users className="w-2.5 h-2.5" style={{ color: '#8B5CF6' }} />
                  </div>
                  <div>
                    <div className="text-[7px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                      {agent.name}
                    </div>
                    <div className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                      {agent.status}
                    </div>
                  </div>
                </div>
                <span 
                  className="text-[6px] px-1.5 py-0.5 rounded flex items-center gap-0.5"
                  style={{ backgroundColor: '#10B98120', color: '#10B981' }}
                >
                  <span className="w-1 h-1 rounded-full bg-[#10B981]" />
                  {agent.type}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

export default AIAgentsContent;
